import React from 'react';
import { Clock, Edit, Eye, EyeOff, Package, PlusCircle, Star, Trash2 } from 'lucide-react';

const ProductsGrid = ({ getCategoryName, handleDelete, handleEdit, openModal, products, selectedCategoryFilter, showProductImages, toggleAvailability, toggleFeatured }) => {
  return (
    <>
      <div className={`grid gap-3 sm:gap-4 ${
        showProductImages 
          ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' 
          : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
      }`}>
        {products
          .filter(product => 
            selectedCategoryFilter === 'all' || product.category_id === selectedCategoryFilter
          )
          .map((product) => (
          <div 
            key={product.id} 
            className={`glass-card product-card min-w-0 ${!showProductImages ? 'flex items-center gap-4' : ''}`} 
            style={{ padding: showProductImages ? '16px' : '12px 16px' }}
          >
            {/* Imagem - Só mostra quando showProductImages é true */}
            {showProductImages && product.image_url && (
              <div className="w-full h-40 rounded-lg overflow-hidden mb-3">
                <img 
                  src={product.image_url} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg";
                  }}
                />
              </div>
            )}
            
            {/* Conteúdo - Layout diferente baseado no modo */}
            <div className={`${!showProductImages ? 'flex-1 min-w-0 flex items-center justify-between gap-4' : ''}`}>
              <div className={`${!showProductImages ? 'flex-1 min-w-0' : 'flex items-start justify-between mb-2'}`}>
                <div className="flex-1 min-w-0">
                  <h3 className={`font-semibold text-white truncate ${showProductImages ? 'text-base mb-1' : 'text-sm'}`}>{product.name}</h3>
                  <p className="text-xs text-gray-400 mb-1">{getCategoryName(product.category_id)}</p>
                  {showProductImages && (
                    <p className="text-xl font-bold text-green-400">R$ {product.price.toFixed(2)}</p>
                  )}
                </div>
                
                {/* Preço e Botões no modo compacto ficam na mesma linha */}
                {!showProductImages && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <p className="text-lg font-bold text-green-400 whitespace-nowrap">R$ {product.price.toFixed(2)}</p>
                  </div>
                )}
              </div>
              
              {/* Botões de Ação */}
              <div className={`flex items-center gap-1 ${showProductImages ? '' : 'flex-shrink-0'}`}>
                {/* Botão de Destaque */}
                <button
                  onClick={() => toggleFeatured(product.id, product.featured)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    product.featured 
                      ? 'text-orange-400 bg-orange-400/20 hover:bg-orange-400/30' 
                      : 'text-gray-400 hover:bg-gray-700 hover:text-orange-400'
                  }`}
                  title={product.featured ? 'Remover destaque' : 'Adicionar aos destaques'}
                  data-testid={`toggle-featured-${product.id}`}
                >
                  <Star size={16} className={product.featured ? 'fill-current' : ''} />
                </button>
                <button
                  onClick={() => toggleAvailability(product.id)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    product.available 
                      ? 'text-green-400 hover:bg-green-400/20' 
                      : 'text-red-400 hover:bg-red-400/20'
                  }`}
                  title={product.available ? 'Disponível' : 'Indisponível'}
                  data-testid={`toggle-availability-${product.id}`}
                >
                  {product.available ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button
                  onClick={() => handleEdit(product)}
                  className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-400/20 rounded-lg transition-colors"
                  title="Editar"
                  data-testid={`edit-product-${product.id}`}
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => handleDelete(product.id)}
                  className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-400/20 rounded-lg transition-colors"
                  title="Excluir"
                  data-testid={`delete-product-${product.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            {/* Descrição e badges - Só no modo com imagens */}
            {showProductImages && product.description && (
              <p className="text-gray-400 text-xs mb-2 line-clamp-2">{product.description}</p>
            )}

            {/* Badges de tempo e disponibilidade - Só no modo com imagens */}
            {showProductImages && (
              <div className="flex items-center justify-between flex-wrap gap-1.5">
                {product.preparation_time && (
                  <div className="flex items-center text-gray-400 text-xs">
                    <Clock size={12} className="mr-1" />
                    {product.preparation_time}min
                  </div>
                )}
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  product.available 
                    ? 'bg-green-400/20 text-green-400' 
                    : 'bg-red-400/20 text-red-400'
                }`}>
                  {product.available ? 'Disponível' : 'Indisponível'}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty State */}
      {products.length === 0 && (
        <div className="glass-card text-center py-12 px-4">
          <Package size={56} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Nenhum produto encontrado</h3>
          <p className="text-gray-400 mb-6">Adicione seus primeiros produtos ao cardápio</p>
          <button 
            onClick={openModal} 
            className="glass-button-primary py-3 px-6"
            data-testid="create-first-product-button"
          >
            <PlusCircle size={20} className="mr-2" />
            Criar Primeiro Produto
          </button>
        </div>
      )}
    </>
  );
};

export default ProductsGrid;
